import { useEffect, useRef, useState } from 'react';

// Ad configurations mapped by slot
const adConfigs = {
  'native-banner': {
    type: 'native',
    script: 'https://pl29192392.profitablecpmratenetwork.com/807a195a2292111bb3f06cec74d01bad/invoke.js',
    containerId: 'container-807a195a2292111bb3f06cec74d01bad'
  },
  'banner-728x90': {
    type: 'iframe',
    key: '9563159b395a614094b8dc52ea0ddb8b',
    script: 'https://www.highperformanceformat.com/9563159b395a614094b8dc52ea0ddb8b/invoke.js',
    height: 90,
    width: 728
  },
  'banner-320x50': {
    type: 'iframe',
    key: '157a668cf7eb01f5a7683aae59088da1',
    script: 'https://www.highperformanceformat.com/157a668cf7eb01f5a7683aae59088da1/invoke.js',
    height: 50,
    width: 320
  },
  'banner-300x250': {
    type: 'iframe',
    key: '26ff2df5f5d186f4f125ea3f6ee3f867',
    script: 'https://www.highperformanceformat.com/26ff2df5f5d186f4f125ea3f6ee3f867/invoke.js',
    height: 250,
    width: 300
  },
  // Aliases for common usage
  'banner': 'banner-728x90',
  'medium-rectangle': 'banner-300x250'
};

export default function AdsDisplay({ adSlot = 'banner', className = '' }) {
  const mountRef = useRef(null);
  const nativeContainerIdRef = useRef(`adsterra-native-${Math.random().toString(36).slice(2, 10)}`);
  const [viewportWidth, setViewportWidth] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth;
  });

  // Resolve slot alias
  let config = adConfigs[adSlot];
  if (typeof config === 'string') {
    config = adConfigs[config];
  }
  
  if (!config) {
    console.warn(`Ad slot "${adSlot}" not found`);
    return null;
  }

  // Track viewport width for responsive scaling
  useEffect(() => {
    const updateViewportWidth = () => setViewportWidth(window.innerWidth);
    updateViewportWidth();
    window.addEventListener('resize', updateViewportWidth);
    return () => window.removeEventListener('resize', updateViewportWidth);
  }, []);

  const availableWidth = typeof viewportWidth === 'number' ? Math.max(viewportWidth - 32, 0) : config.width;
  const adScale = config.type === 'iframe' && config.width
    ? Math.min(1, availableWidth / config.width)
    : 1;
  const scaledHeight = config.type === 'iframe' && config.height
    ? Math.ceil(config.height * adScale)
    : undefined;
  const scaledWidth = config.type === 'iframe' && config.width
    ? Math.ceil(config.width * adScale)
    : undefined;

  useEffect(() => {
    const mountNode = mountRef.current;
    if (!mountNode) return undefined;

    mountNode.innerHTML = '';

    if (config.type === 'native') {
      const container = document.createElement('div');
      container.id = nativeContainerIdRef.current;
      container.className = 'ad-slot';
      mountNode.appendChild(container);

      const script = document.createElement('script');
      script.async = true;
      script.src = config.script;
      script.setAttribute('data-cfasync', 'false');
      mountNode.appendChild(script);

      return () => {
        mountNode.innerHTML = '';
      };
    }

    const hostFrame = document.createElement('iframe');
    hostFrame.title = `adsterra-${config.key}`;
    hostFrame.width = String(config.width);
    hostFrame.height = String(config.height);
    hostFrame.scrolling = 'no';
    hostFrame.frameBorder = '0';
    hostFrame.allow = 'autoplay';
    hostFrame.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox');
    hostFrame.style.border = '0';
    hostFrame.style.overflow = 'hidden';
    hostFrame.style.width = `${config.width}px`;
    hostFrame.style.height = `${config.height}px`;
    hostFrame.style.display = 'block';
    hostFrame.style.margin = '0 auto';

    mountNode.appendChild(hostFrame);

    const injectAd = () => {
      const doc = hostFrame.contentWindow?.document;
      if (!doc) return;

      doc.open();
      doc.write(`
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <style>
              html, body {
                margin: 0;
                padding: 0;
                width: ${config.width}px;
                height: ${config.height}px;
                overflow: hidden;
                background: transparent;
              }
            </style>
          </head>
          <body>
            <script>
              var atOptions = {
                key: '${config.key}',
                format: 'iframe',
                height: ${config.height},
                width: ${config.width},
                params: {}
              };
            </script>
            <script async src="${config.script}"><\/script>
          </body>
        </html>
      `);
      doc.close();
    };

    hostFrame.addEventListener('load', injectAd, { once: true });
    if (hostFrame.contentWindow?.document?.readyState === 'complete') {
      injectAd();
    }

    return () => {
      hostFrame.removeEventListener('load', injectAd);
      mountNode.innerHTML = '';
    };
  }, [config]);

  if (config.type === 'native') {
    return (
      <div ref={mountRef} className={`ads-container ${className}`} style={{ minHeight: 'auto' }}>
      </div>
    );
  }

  return (
    <div
      className={`ads-container ${className}`}
      style={{
        minHeight: (scaledHeight || config.height) + 20,
        width: '100%',
        maxWidth: '100%',
      }}
    >
      <div
        className="ads-frame-shell"
        style={{
          width: scaledWidth || config.width,
          height: scaledHeight || config.height,
          margin: '0 auto',
        }}
      >
        <div
          className="ads-frame-wrapper"
          style={{
            width: config.width,
            height: config.height,
            transform: `scale(${adScale})`,
            transformOrigin: 'top left',
          }}
        >
          <div
            ref={mountRef}
            style={{
              width: config.width,
              height: config.height,
            }}
          />
        </div>
      </div>
    </div>
  );
}
